import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Toast,
  NavBar,
  Selector,
  ImageUploader,
  Dialog,
  List,
} from 'antd-mobile';
import { ImageUploadItem } from 'antd-mobile/es/components/image-uploader';
import { productService } from '../services/productService';
import { brandService } from '../services/brandService';
import { uploadService } from '../services/uploadService';
import { Brand } from '../types';
import './ProductEdit.css';

const ProductEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [fileList, setFileList] = useState<ImageUploadItem[]>([]);
  const [colorList, setColorList] = useState<string[]>([]); // 颜色数组，与图片对应

  const isEditMode = id && id !== 'new';

  useEffect(() => {
    loadBrands();
    if (isEditMode) {
      loadProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadBrands = async () => {
    try {
      const data = await brandService.getActiveBrands();
      setBrands(data);
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: '加载品牌失败',
      });
    }
  };

  const loadProduct = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const product = await productService.getProductById(id);
      form.setFieldsValue({
        product_name: product.product_name,
        product_code: product.product_code,
        product_price: product.product_price,
        brand_id: [product.brand_id], // Selector 需要数组格式
        product_type: product.product_type,
        product_unit: product.product_unit,
      });
      // 加载多张图片
      if (product.product_images && product.product_images.length > 0) {
        setFileList(product.product_images.map(url => ({ url })));
      }
      // 加载颜色数组
      if (product.product_colors && product.product_colors.length > 0) {
        setColorList(product.product_colors);
      }
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: '加载商品失败',
      });
    } finally {
      setLoading(false);
    }
  };

  // 当图片列表变化时，同步调整颜色列表长度
  useEffect(() => {
    setColorList(prev => {
      const newList = [...prev];
      // 如果图片数量增加，添加空颜色
      while (newList.length < fileList.length) {
        newList.push('');
      }
      // 如果图片数量减少，删除多余颜色
      if (newList.length > fileList.length) {
        newList.length = fileList.length;
      }
      return newList;
    });
  }, [fileList.length]);

  const handleColorChange = (index: number, value: string) => {
    const newColorList = [...colorList];
    newColorList[index] = value;
    setColorList(newColorList);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 处理 brand_id：Selector 返回数组，我们需要第一个值
      const brandId = Array.isArray(values.brand_id) 
        ? values.brand_id[0] 
        : values.brand_id;

      // 收集所有图片 URL
      const imageUrls = fileList.map(file => file.url).filter(Boolean) as string[];

      // 过滤掉空颜色，保持与图片数量一致
      const colors = colorList.slice(0, imageUrls.length);

      const productData = {
        ...values,
        brand_id: brandId,
        product_price: parseFloat(values.product_price),
        product_images: imageUrls, // 保存多张图片
        product_colors: colors.length > 0 ? colors : null, // 保存颜色数组
      };

      if (isEditMode && id) {
        await productService.updateProduct(id, productData);
        Toast.show({
          icon: 'success',
          content: '更新成功',
        });
      } else {
        await productService.createProduct(productData);
        Toast.show({
          icon: 'success',
          content: '创建成功',
        });
      }
      navigate('/');
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: isEditMode ? '更新失败' : '创建失败',
      });
      console.error('Error saving product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    const result = await Dialog.confirm({
      content: '确定要删除这个商品吗？',
    });

    if (result) {
      try {
        setLoading(true);
        await productService.deleteProduct(id);
        Toast.show({
          icon: 'success',
          content: '删除成功',
        });
        navigate('/');
      } catch (error) {
        Toast.show({
          icon: 'fail',
          content: '删除失败',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpload = async (file: File) => {
    try {
      Toast.show({
        icon: 'loading',
        content: '上传中...',
        duration: 0,
      });
      
      // 上传到 Supabase Storage
      const url = await uploadService.uploadImage(file, 'product-images');
      
      Toast.clear();
      
      if (url.includes('placeholder')) {
        Toast.show({
          icon: 'success',
          content: '使用占位图片（Storage 未配置）',
          duration: 2000,
        });
      } else {
        Toast.show({
          icon: 'success',
          content: '上传成功',
          duration: 1000,
        });
      }
      
      return { url };
    } catch (error) {
      Toast.clear();
      console.error('Upload failed:', error);
      Toast.show({
        icon: 'fail',
        content: '上传失败，使用占位图片',
        duration: 2000,
      });
      // 返回占位图片
      return {
        url: `https://via.placeholder.com/300x300/4A90E2/ffffff?text=${encodeURIComponent(file.name.substring(0, 10))}`,
      };
    }
  };

  return (
    <div className="product-edit-container">
      <NavBar onBack={() => navigate('/')}>
        {isEditMode ? '编辑商品' : '新建商品'}
      </NavBar>

      <div className="form-container">
        <Form
          form={form}
          layout="horizontal"
        >
          <Form.Item
            name="product_name"
            label="商品名称"
            rules={[{ required: true, message: '请输入商品名称' }]}
          >
            <Input placeholder="请输入商品名称" />
          </Form.Item>

          <Form.Item
            name="product_code"
            label="商品编码"
            rules={[{ required: true, message: '请输入商品编码' }]}
          >
            <Input placeholder="请输入商品编码" />
          </Form.Item>

          <Form.Item
            name="product_price"
            label="商品价格"
            rules={[{ required: true, message: '请输入商品价格' }]}
          >
            <Input type="number" placeholder="请输入商品价格" />
          </Form.Item>

          <Form.Item
            name="brand_id"
            label="所属品牌"
            rules={[{ required: true, message: '请选择品牌' }]}
          >
            <Selector
              options={brands.map((brand) => ({
                label: brand.brand_name,
                value: brand.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="product_type"
            label="商品类型"
            rules={[{ required: true, message: '请输入商品类型' }]}
          >
            <Input placeholder="请输入商品类型" />
          </Form.Item>

          <Form.Item
            name="product_unit"
            label="商品单位"
            rules={[{ required: true, message: '请输入商品单位' }]}
          >
            <Input placeholder="如：件、个、箱等" />
          </Form.Item>

          <Form.Item label="商品图片">
            <ImageUploader
              value={fileList}
              onChange={setFileList}
              upload={handleUpload}
              maxCount={9}
              multiple
            />
          </Form.Item>

          {fileList.length > 0 && (
            <Form.Item label="图片颜色">
              <List>
                {fileList.map((file, index) => (
                  <List.Item
                    key={index}
                    prefix={
                      <img 
                        src={file.url} 
                        alt={`图片${index + 1}`}
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                      />
                    }
                    description={`图片 ${index + 1}`}
                  >
                    <Input
                      placeholder={`颜色 ${index + 1}（可选）`}
                      value={colorList[index] || ''}
                      onChange={(val) => handleColorChange(index, val)}
                    />
                  </List.Item>
                ))}
              </List>
            </Form.Item>
          )}
        </Form>
      </div>

      <div className="form-footer">
        <Button
          block
          type="submit"
          color="primary"
          size="large"
          loading={loading}
          onClick={handleSubmit}
        >
          {isEditMode ? '保存' : '创建'}
        </Button>
        {isEditMode && (
          <Button
            block
            color="danger"
            size="large"
            style={{ marginTop: '12px' }}
            onClick={handleDelete}
          >
            删除商品
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductEdit;
